import { useQuery } from '@tanstack/react-query'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { ApiError } from '@/lib/api'
import { formatMoment } from '@/card/recordHelpers'
import { fetchCounterpartyRisk } from './clientRiskApi'
import { LevelBadge } from './ClientRiskPage'

/**
 * Риск клиента на его карточке (#135): уровень, балл и причины словами сервера.
 *
 * Рисуется только при уровне `high` или `medium`: «рисков нет» на каждой карточке — шум, который
 * учат пропускать, и тогда пропускают и настоящий риск. Ошибка — словами, а не пустотой: пустота
 * читается как «всё хорошо». Сервер пересчитывает риск при каждом открытии карточки.
 *
 * Без права `sales.risk:read` панели нет и запрос не уходит: риск — своё право (решение
 * владельца), и оно может быть не выдано тому, кто читает клиента.
 */
export function ClientRiskPanel({ counterpartyId }: { counterpartyId: string }) {
  const { t, user, locale } = useApp()
  const allowed = can(user, 'sales.risk', 'read')
  const query = useQuery({
    queryKey: ['client-risk-card', counterpartyId],
    queryFn: ({ signal }) => fetchCounterpartyRisk(counterpartyId, signal),
    retry: false,
    enabled: allowed,
  })
  if (!allowed) return null
  if (query.isError) {
    return (
      <p role="alert" className="t-sm">
        {query.error instanceof ApiError ? query.error.message : t('clientRisk.error')}
      </p>
    )
  }
  const risk = query.data?.risk
  if (risk == null || risk.level === 'low') return null
  return (
    <section className={risk.level === 'high' ? 'alert alert-danger' : 'alert alert-warning'}
             role="status" aria-label={t('clientRisk.card.title')} data-testid="client-risk">
      <div className="stack" style={{ gap: 6 }}>
        <div className="row" style={{ gap: 8, alignItems: 'center' }}>
          <span className="strong">{t('clientRisk.card.title')}</span>
          <LevelBadge level={risk.level} />
          <span className="muted t-sm">{t('clientRisk.card.score', { score: risk.score })}</span>
          {risk.riskSince != null && (
            <span className="muted t-sm">{t('clientRisk.card.since', { at: formatMoment(risk.riskSince, locale) })}</span>
          )}
        </div>
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {(risk.reasons ?? []).map((reason) => <li key={reason.code}>{reason.text}</li>)}
        </ul>
        <span className="t-sm muted">{t('clientRisk.card.rule')}</span>
      </div>
    </section>
  )
}
